import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Divider,
  Alert,
  Badge,
  Tooltip,
  Popconfirm,
  Upload,
  Steps,
  Progress,
  Descriptions
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PrinterOutlined,
  DownloadOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  CalendarOutlined,
  MailOutlined,
  CopyOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Invoice, InvoiceLine, BusinessPartner, Product, Tax } from '../../types';
import apiService from '../../../../services/apiService';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface InvoiceStats {
  totalInvoices: number;
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
  overdueAmount: number;
}

const InvoiceManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('sales');
  const [salesInvoices, setSalesInvoices] = useState<Invoice[]>([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState<Invoice[]>([]);
  const [customers, setCustomers] = useState<BusinessPartner[]>([]);
  const [suppliers, setSuppliers] = useState<BusinessPartner[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [loading, setLoading] = useState(false);
  const [isInvoiceModalVisible, setIsInvoiceModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoiceLines, setInvoiceLines] = useState<Partial<InvoiceLine>[]>([]);
  const [invoiceStats, setInvoiceStats] = useState<InvoiceStats>({
    totalInvoices: 0,
    totalAmount: 0,
    paidAmount: 0,
    pendingAmount: 0,
    overdueAmount: 0
  });

  const [form] = Form.useForm();

  // 初始化從後端載入列表
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // 暫時不帶 companyId 條件，避免後端過濾導致無資料
        const resp: any = await apiService.getInvoices({ page: 1, limit: 10 });
        const rows: any[] = Array.isArray(resp?.invoices) ? resp.invoices : (Array.isArray(resp) ? resp : []);
        const mapped: Invoice[] = (rows||[]).map((r: any) => ({
          id: String(r.id),
          invoiceNumber: r.invoiceNo || r.invoiceNumber,
          type: 'out_invoice',
          partnerId: String(r.customerId || ''),
          partner: { id: String(r.customer?.id||''), name: r.customer?.name||'', displayName: r.customer?.name||'', isCompany: true, children: [], email: r.customer?.email||'', phone: r.customer?.phone||'', isCustomer: true, isSupplier: false, customerRank: 0, supplierRank: 0, category: [], active: true, createdAt: new Date(), updatedAt: new Date() },
          invoiceDate: r.invoiceDate ? new Date(r.invoiceDate) : new Date(r.createdAt),
          dueDate: r.dueDate ? new Date(r.dueDate) : undefined,
          state: String(r.status || 'draft').toLowerCase(),
          lines: [],
          taxLines: [],
          subtotalAmount: Number(r.totalAmount || 0),
          taxAmount: 0,
          totalAmount: Number(r.totalAmount || 0),
          residualAmount: Number(r.totalAmount || 0),
          paidAmount: 0,
          currency: 'USD',
          payments: [],
          createdAt: new Date(r.createdAt),
          updatedAt: new Date(r.updatedAt)
        }));
        setSalesInvoices(mapped);
        setInvoiceStats({
          totalInvoices: mapped.length,
          totalAmount: mapped.reduce((s, x) => s + (x.totalAmount||0), 0),
          paidAmount: mapped.reduce((s, x) => s + (x.paidAmount||0), 0),
          pendingAmount: mapped.reduce((s, x) => s + (x.residualAmount||0), 0),
          overdueAmount: mapped.filter(x => x.dueDate && dayjs(x.dueDate).isBefore(dayjs()) && (x.residualAmount||0) > 0).reduce((s,x)=> s + (x.residualAmount||0), 0)
        });
      } catch (e) {
        setSalesInvoices([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getInvoiceStatusColor = (state: string) => {
    switch (state) {
      case 'draft': return 'default';
      case 'open': return 'orange';
      case 'paid': return 'green';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const getPaymentStatus = (invoice: Invoice) => {
    if (invoice.paidAmount === 0) return { status: 'unpaid', color: 'red' };
    if (invoice.paidAmount >= invoice.totalAmount) return { status: 'paid', color: 'green' };
    return { status: 'partial', color: 'orange' };
  };

  const handleCreateInvoice = async (values: any) => {
    try {
      setLoading(true);
      // API call would go here
      message.success('Invoice created successfully');
      setIsInvoiceModalVisible(false);
      form.resetFields();
      setInvoiceLines([]);
    } catch (error) {
      message.error('Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  // 查看詳情
  const handleViewInvoice = async (record: Invoice) => {
    try {
      setLoading(true);
      const resp: any = await apiService.getInvoiceById(record.id);
      const r = resp?.invoice || resp || record;
      const mapped: Invoice = {
        id: String(r.id),
        invoiceNumber: r.invoiceNo || r.invoiceNumber,
        type: 'out_invoice',
        partnerId: String(r.customerId || r.partnerId || ''),
        partner: r.customer || r.partner || { id: String(r.customerId||''), name: r.customer?.name||'', displayName: r.customer?.name||'', isCompany: true, children: [], email: r.customer?.email||'', phone: r.customer?.phone||'', isCustomer: true, isSupplier: false, customerRank: 0, supplierRank: 0, category: [], active: true, createdAt: new Date(), updatedAt: new Date() },
        invoiceDate: r.invoiceDate ? new Date(r.invoiceDate) : (r.createdAt ? new Date(r.createdAt) : new Date()),
        dueDate: r.dueDate ? new Date(r.dueDate) : new Date(),
        state: String(r.status || 'draft').toLowerCase(),
        lines: Array.isArray(r.items) ? r.items.map((it: any) => ({
          id: String(it.id || `${r.id}-${Math.random()}`),
          invoiceId: String(r.id),
          productId: String(it.productId || ''),
          product: it.product || undefined,
          name: it.name || it.product?.itemName || '',
          description: it.description || '',
          quantity: Number(it.quantity || 0),
          unitPrice: Number(it.unitPrice || 0),
          discount: 0,
          subtotal: Number(it.lineTotal || (Number(it.quantity||0)*Number(it.unitPrice||0))),
          accountId: '',
          account: {} as any,
          taxIds: [],
          taxes: []
        })) : [],
        taxLines: [],
        subtotalAmount: Number(r.totalAmount || 0),
        taxAmount: 0,
        totalAmount: Number(r.totalAmount || 0),
        residualAmount: Number(r.residualAmount || r.totalAmount || 0),
        paidAmount: Number(r.paidAmount || 0),
        currency: 'USD',
        payments: [],
        createdAt: new Date(r.createdAt),
        updatedAt: new Date(r.updatedAt)
      };
      setSelectedInvoice(mapped);
    } catch {
      setSelectedInvoice(record);
    } finally {
      setLoading(false);
    }
  };

  // 編輯（回顯到表單）
  const handleEditInvoice = async (record: Invoice) => {
    try {
      setLoading(true);
      const resp: any = await apiService.getInvoiceById(record.id);
      const r = resp?.invoice || resp || record;
      setSelectedInvoice(r);
      setIsInvoiceModalVisible(true);
      form.setFieldsValue({
        invoiceNumber: r.invoiceNo || r.invoiceNumber,
        invoiceDate: r.invoiceDate ? dayjs(r.invoiceDate) : (r.createdAt ? dayjs(r.createdAt) : undefined),
        partnerId: r.customerId || r.partnerId,
        dueDate: r.dueDate ? dayjs(r.dueDate) : undefined,
        description: r.description || ''
      });
      const mappedLines: Partial<InvoiceLine>[] = Array.isArray(r.items) ? r.items.map((it: any) => ({
        productId: String(it.productId || ''),
        name: it.name || it.product?.itemName || '',
        quantity: Number(it.quantity || 0),
        unitPrice: Number(it.unitPrice || 0),
        discount: 0,
        subtotal: Number(it.lineTotal || (Number(it.quantity||0)*Number(it.unitPrice||0))),
        accountId: '',
        taxIds: []
      })) : [];
      setInvoiceLines(mappedLines);
    } catch {
      setIsInvoiceModalVisible(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoice = async (invoiceId: string) => {
    try {
      setLoading(true);
      // API call would go here
      message.success('Invoice sent successfully');
    } catch (error) {
      message.error('Failed to send invoice');
    } finally {
      setLoading(false);
    }
  };

  const addInvoiceLine = () => {
    setInvoiceLines([
      ...invoiceLines,
      {
        productId: '',
        name: '',
        quantity: 1,
        unitPrice: 0,
        discount: 0,
        subtotal: 0,
        accountId: '',
        taxIds: []
      }
    ]);
  };

  const removeInvoiceLine = (index: number) => {
    const newLines = invoiceLines.filter((_, i) => i !== index);
    setInvoiceLines(newLines);
  };

  const updateInvoiceLine = (index: number, field: string, value: any) => {
    const newLines = [...invoiceLines];
    newLines[index] = { ...newLines[index], [field]: value };
    
    // Calculate subtotal
    if (field === 'quantity' || field === 'unitPrice' || field === 'discount') {
      const line = newLines[index];
      const quantity = line.quantity || 0;
      const unitPrice = line.unitPrice || 0;
      const discount = line.discount || 0;
      newLines[index].subtotal = quantity * unitPrice * (1 - discount / 100);
    }
    
    setInvoiceLines(newLines);
  };

  const salesInvoiceColumns: ColumnsType<Invoice> = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (text, record) => (
        <Button type="link" onClick={() => setSelectedInvoice(record)}>
          {text}
        </Button>
      )
    },
    {
      title: 'Customer',
      dataIndex: ['partner', 'name'],
      key: 'customer'
    },
    {
      title: 'Invoice Date',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      render: (date) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => `$${amount.toLocaleString()}`
    },
    {
      title: 'Paid Amount',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (amount) => `$${amount.toLocaleString()}`
    },
    {
      title: 'Balance',
      dataIndex: 'residualAmount',
      key: 'residualAmount',
      render: (amount) => `$${amount.toLocaleString()}`
    },
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      render: (_, record) => {
        const status = getPaymentStatus(record);
        return (
          <Tag color={status.color}>
            {status.status.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      render: (status) => (
        <Tag color={getInvoiceStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewInvoice(record)} />
          {record.state === 'draft' && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditInvoice(record)}
            />
          )}
          <Button
            type="text"
            icon={<PrinterOutlined />}
            onClick={() => {/* Print logic */}}
          />
          <Button
            type="text"
            icon={<MailOutlined />}
            onClick={() => handleSendInvoice(record.id)}
          />
          <Button
            type="text"
            icon={<CopyOutlined />}
            onClick={() => {/* Duplicate logic */}}
          />
        </Space>
      )
    }
  ];

  const invoiceLineColumns: ColumnsType<Partial<InvoiceLine>> = [
    {
      title: 'Product/Service',
      dataIndex: 'productId',
      key: 'productId',
      render: (_, record, index) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Select product"
          value={record.productId}
          onChange={(value) => {
            updateInvoiceLine(index, 'productId', value);
            const product = products.find(p => p.id === value);
            if (product) {
              updateInvoiceLine(index, 'name', product.name);
              updateInvoiceLine(index, 'unitPrice', product.listPrice);
            }
          }}
          showSearch
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
        >
          {products.map(product => (
            <Option key={product.id} value={product.id}>
              {product.name}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: 'Description',
      dataIndex: 'name',
      key: 'name',
      render: (_, record, index) => (
        <Input
          value={record.name}
          onChange={(e) => updateInvoiceLine(index, 'name', e.target.value)}
          placeholder="Enter description"
        />
      )
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (_, record, index) => (
        <InputNumber
          value={record.quantity}
          onChange={(value) => updateInvoiceLine(index, 'quantity', value || 0)}
          min={0}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      render: (_, record, index) => (
        <InputNumber
          value={record.unitPrice}
          onChange={(value) => updateInvoiceLine(index, 'unitPrice', value || 0)}
          min={0}
          style={{ width: '100%' }}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
        />
      )
    },
    {
      title: 'Discount %',
      dataIndex: 'discount',
      key: 'discount',
      render: (_, record, index) => (
        <InputNumber
          value={record.discount}
          onChange={(value) => updateInvoiceLine(index, 'discount', value || 0)}
          min={0}
          max={100}
          style={{ width: '100%' }}
          formatter={(value) => `${value}%`}
          parser={(value) => parseFloat(value!.replace('%', '')) || 0}
        />
      )
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      render: (subtotal) => `$${(subtotal || 0).toLocaleString()}`
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record, index) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          danger
          onClick={() => removeInvoiceLine(index)}
        />
      )
    }
  ];

  const renderInvoiceModal = () => (
    <Modal
      title={selectedInvoice ? 'Edit Invoice' : 'Create Invoice'}
      open={isInvoiceModalVisible}
      onCancel={() => {
        setIsInvoiceModalVisible(false);
        form.resetFields();
        setInvoiceLines([]);
        setSelectedInvoice(null);
      }}
      footer={null}
      width={1200}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleCreateInvoice}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="invoiceNumber"
              label="Invoice Number"
              rules={[{ required: true, message: 'Please enter invoice number' }]}
            >
              <Input placeholder="INV-001" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="invoiceDate"
              label="Invoice Date"
              rules={[{ required: true, message: 'Please select date' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="partnerId"
              label={activeTab === 'sales' ? 'Customer' : 'Supplier'}
              rules={[{ required: true, message: 'Please select customer/supplier' }]}
            >
              <Select placeholder={`Select ${activeTab === 'sales' ? 'customer' : 'supplier'}`} showSearch>
                {(activeTab === 'sales' ? customers : suppliers).map(partner => (
                  <Option key={partner.id} value={partner.id}>
                    {partner.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="dueDate"
              label="Due Date"
              rules={[{ required: true, message: 'Please select due date' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="reference" label="Reference">
              <Input placeholder="PO-001" />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={5}>Invoice Lines</Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={addInvoiceLine}>
            Add Line
          </Button>
        </div>

        <Table
          columns={invoiceLineColumns}
          dataSource={invoiceLines}
          pagination={false}
          rowKey={(record, index) => index}
          style={{ marginBottom: 16 }}
        />

        <Row gutter={16} justify="end">
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Subtotal"
                value={invoiceLines.reduce((sum, line) => sum + (line.subtotal || 0), 0)}
                prefix="$"
                precision={2}
              />
              <Statistic
                title="Tax"
                value={0}
                prefix="$"
                precision={2}
                style={{ marginTop: 16 }}
              />
              <Divider style={{ margin: '8px 0' }} />
              <Statistic
                title="Total"
                value={invoiceLines.reduce((sum, line) => sum + (line.subtotal || 0), 0)}
                prefix="$"
                precision={2}
              />
            </Card>
          </Col>
        </Row>

        <div style={{ textAlign: 'right', marginTop: 24 }}>
          <Space>
            <Button onClick={() => setIsInvoiceModalVisible(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Create Invoice
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );

  const renderInvoiceDetail = () => (
    <Modal
      title={`Invoice ${selectedInvoice?.invoiceNumber}`}
      open={!!selectedInvoice && !isInvoiceModalVisible}
      onCancel={() => setSelectedInvoice(null)}
      footer={
        <Space>
          <Button onClick={() => setSelectedInvoice(null)}>Close</Button>
          <Button icon={<PrinterOutlined />}>Print</Button>
          <Button type="primary" icon={<MailOutlined />}>Send</Button>
        </Space>
      }
      width={800}
    >
      {selectedInvoice && (
        <div>
          <Descriptions column={2}>
            <Descriptions.Item label="Invoice Number">
              {selectedInvoice.invoiceNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getInvoiceStatusColor(selectedInvoice.state)}>
                {selectedInvoice.state.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Customer">
              {selectedInvoice.partner.name}
            </Descriptions.Item>
            <Descriptions.Item label="Invoice Date">
              {dayjs(selectedInvoice.invoiceDate).format('YYYY-MM-DD')}
            </Descriptions.Item>
            <Descriptions.Item label="Due Date">
              {dayjs(selectedInvoice.dueDate).format('YYYY-MM-DD')}
            </Descriptions.Item>
            <Descriptions.Item label="Total Amount">
              ${selectedInvoice.totalAmount.toLocaleString()}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={5}>Payment Progress</Title>
          <Progress
            percent={(selectedInvoice.paidAmount / selectedInvoice.totalAmount) * 100}
            format={() => `$${selectedInvoice.paidAmount} / $${selectedInvoice.totalAmount}`}
          />
        </div>
      )}
    </Modal>
  );

  const renderSalesTab = () => (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Invoices"
              value={invoiceStats.totalInvoices}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Amount"
              value={invoiceStats.totalAmount}
              prefix="$"
              precision={2}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Paid Amount"
              value={invoiceStats.paidAmount}
              prefix="$"
              precision={2}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Outstanding"
              value={invoiceStats.pendingAmount}
              prefix="$"
              precision={2}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Sales Invoices"
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsInvoiceModalVisible(true)}>
              New Invoice
            </Button>
            <Button icon={<UploadOutlined />}>Import</Button>
            <Button icon={<DownloadOutlined />}>Export</Button>
          </Space>
        }
      >
        <Table
          columns={salesInvoiceColumns}
          dataSource={salesInvoices}
          rowKey="id"
          loading={loading}
        />
      </Card>
    </div>
  );

  const renderPurchaseTab = () => (
    <Card
      title="Purchase Invoices"
      extra={
        <Button type="primary" icon={<PlusOutlined />}>
          New Purchase Invoice
        </Button>
      }
    >
      <Table
        columns={salesInvoiceColumns}
        dataSource={purchaseInvoices}
        rowKey="id"
        loading={loading}
      />
    </Card>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Invoice Management</Title>
        <Text type="secondary">
          Manage sales and purchase invoices, track payments
        </Text>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Sales Invoices" key="sales">
          {renderSalesTab()}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Purchase Invoices" key="purchase">
          {renderPurchaseTab()}
        </Tabs.TabPane>
      </Tabs>

      {renderInvoiceModal()}
      {renderInvoiceDetail()}
    </div>
  );
};

export default InvoiceManagement; 